import { useRef, useMemo, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useSettings } from '@/SettingsContext';
import { useLanguage } from '@/LanguageContext';

// ====================================================================
//
//   ██╗  ██╗███████╗██████╗  ██████╗     ███╗   ███╗ █████╗ ███████╗████████╗███████╗██████╗
//   ██║  ██║██╔════╝██╔══██╗██╔═══██╗    ████╗ ████║██╔══██╗██╔════╝╚══██╔══╝██╔════╝██╔══██╗
//   ███████║█████╗  ██████╔╝██║   ██║    ██╔████╔██║███████║███████╗   ██║   █████╗  ██████╔╝
//   ██╔══██║██╔══╝  ██╔══██╗██║   ██║    ██║╚██╔╝██║██╔══██║╚════██║   ██║   ██╔══╝  ██╔══██╗
//   ██║  ██║███████╗██║  ██║╚██████╔╝    ██║ ╚═╝ ██║██║  ██║███████║   ██║   ███████╗██║  ██║
//   ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝ ╚═════╝    ╚═╝     ╚═╝╚═╝  ╚═╝╚══════╝   ╚═╝   ╚══════╝╚═╝  ╚═╝
//
//   Tüm görsel ayarlar bu panelden yönetilir.
//   Her satırın yanındaki yorum, ne işe yaradığını açıklar.
//
// ====================================================================


// API'den ayarları çeker, bulamazsa hardcoded HERO değerlerini kullanır


const HERO = {

  // ─────────────────────────────────────────────
  //  🖼️  ARKAPLAN & OVERLAY
  // ─────────────────────────────────────────────
  media: {
  videoSrc: "/media/videos/test.mp4",  // video dosyanın yolu
  overlayOpacity: "bg-black/70",
  zoomOnScroll: 1.3,
},

  // ─────────────────────────────────────────────
  //  📐  LAYOUT & BOYUTLAR
  // ─────────────────────────────────────────────
  layout: {
    mobileHeight:        "h-[100svh]",
    desktopHeight:       "md:h-screen",
    containerPaddingTop: "pt-20 md:pt-28 lg:pt-32",
    lineGap:             "mb-1 md:mb-3 lg:mb-5",
    horizontalPadding:   "px-6 md:px-12 lg:px-24",
    contentAlign:        "items-start text-left",
  },

  // ─────────────────────────────────────────────
  //  🔤  TİPOGRAFİ — 3 SATIR
  // ─────────────────────────────────────────────
  //
  //  size formatı → "mobil tablet masaüstü"
  //  Örnek: "text-[10vw] md:text-[6vw] lg:text-[6rem]"
  //    vw  = ekran genişliğinin yüzdesi (responsive)
  //    rem = sabit boyut (1rem = 16px genelde)
  //
  //  weight seçenekleri:
  //    font-thin | font-light | font-normal | font-semibold | font-bold | font-black
  //
  //  color için Tailwind renk sınıfı kullan:
  //    text-white | text-white/50 | text-zinc-300 | text-gray-100 vb.
  //    /sayı = opaklık (text-white/20 = %20 opaklık)
  //
  typography: {

    line1: {
      text:   "CREATE",
      //  clamp(min, preferred, max) yerine Tailwind vw sınıfları:
      //  text-[Xvw] = ekran genişliğinin X%'i — mobilde büyük kalır, masaüstünde sınırlanır
      size:   "text-[14vw] md:text-[8vw] lg:text-[4rem]",
      color:  "text-white",
      weight: "font-bold",
    },

    line2: {
      text:   "DIGITAL",               // Bu satır NEON efekti alır — rengini neon.colors'dan alır
      // width: SVG'nin max genişliği. line1 ile aynı görsel boyut için line1'in font-size'ı kadar olmalı.
      // "DIGITAL" 7 harf, bold → genişliği yaklaşık font-size * 5.5
      // 14vw font → genişlik ≈ 77vw | 8vw → ≈ 44vw | 7.5rem → ≈ 41rem
      // Aşağıdaki değeri artırırsan yazı büyür, azaltırsan küçülür.
      width:  "w-[55vw] md:w-[36vw] lg:w-[35rem]",
    },

    line3: {
      text:   "EXPERIENCES",
      //  "EXPERIENCES" daha uzun kelime → biraz daha küçük tutuyoruz ki taşmasın
      size:   "text-[11vw] md:text-[6.5vw] lg:text-[5rem]",
      color:  "text-white/25",         // /20 → çok soluk; /60 → daha belirgin
      weight: "font-bold",
    },

  },

  // ─────────────────────────────────────────────
  //  🌈  NEON EFEKTİ — ANA KONTROL MERKEZİ
  // ─────────────────────────────────────────────
  neon: {

    // RENK PALETİ
    // Gradient bu renklerden oluşur. İstediğin kadar hex ekle.
    // İlk ve son rengi aynı yaparsan sonsuz döngü daha dikişsiz görünür.
    colors: ["#f7640f", "#bc13fe", "#fbff2d", "#0ff796"],

    // ÇİZGİ KALINLIĞI
    // 1 = çok ince tel    |   2 = standart   |   4+ = kalın bant
    strokeWidth: 1.5,

    // PARLAMA (GLOW)
    // glowSize: "8px" = dar odaklı   →   "40px" = geniş yayılan atmosfer
    // glowOpacity: 0.0 (parlama yok) →   1.0 (tam parlama)
    glowSize:    "8px",
    glowOpacity: 1.0,

    // AKIŞ HIZI ARALIĞI (saniye)
    // Her bağımsız segment bu aralıktan rastgele bir hız alır.
    // speedMin düşük = çok hızlı segmentler olabilir
    // speedMax yüksek = çok yavaş segmentler olabilir
    // Fark büyüdükçe kaotik/organik his artar
    speedMin: 5,
    speedMax: 35,

    // SEGMENT SAYISI
    // Kaç bağımsız neon çizgisi üst üste çizilir?
    // 2-3 → sade, minimal
    // 4-5 → dengeli, zengin
    // 6-8 → kaotik, yoğun, katmanlı
    segmentCount: 3,

    // DASH UZUNLUĞU ARALIĞI (piksel)
    // dashArrayMin küçük = kısa parlak nokta/çizgiler
    // dashArrayMax büyük = uzun parlayan segmentler
    // İkisi birbirine yakın = benzer uzunlukta tekdüze segmentler
    // Fark büyük = karışık kısa-uzun, çok organik
    dashArrayMin: 200,
    dashArrayMax: 800,

    // FAZ ARALIĞI
    // Her segmentin animasyona nereden başlayacağını belirler.
    // Büyük aralık → segmentler farklı noktalarda başlar, çakışmaz
    // Küçük aralık → segmentler birbirine yakın kümeleşir
    phaseMin: 0,
    phaseMax: 5000,

    // RENK STOP JITTER (0.0 → 0.3)
    // Gradient geçiş noktalarının ne kadar rastgele kaydığı.
    // 0   = her zaman düzgün dağılım (uniform gradient)
    // 0.1 = hafif organik dalgalanma
    // 0.25= belirgin renk yığılmaları ve açılmaları
    colorJitter: 0.18,

    // HAYALET DOLGU
    // Neon çizgisinin gerisinde çok hafif beyaz dolgu ekler → okunabilirliği artırır
    // showGhostFill: true açmak için
    // ghostFillOpacity: 0.0 (görünmez) → 0.15 (belirgin)
    showGhostFill:      false,
    ghostFillOpacity:   0.03,
  },

  // ─────────────────────────────────────────────
  //  🎬  GİRİŞ & SCROLL ANİMASYONU
  // ─────────────────────────────────────────────
  animation: {
    // SAYFA YÜKLEME GİRİŞİ
    // entryDuration: 1.0 = snappy | 2.5 = sinematik yavaş
    // entryBlur: "0px" = blur yok | "40px" = çok bulanık giriş
    // initialY: kaç piksel aşağıdan süzülerek gelir
    entryDuration: 1.4,
    entryBlur:     "8px",
    initialY:      40,

    // SATIR GECİKMELERİ (stagger — saniye)
    // Birbirini takip eden gecikmeler kademelı giriş hissi yaratır
    line1Delay: 0.1,
    line2Delay: 0.28,
    line3Delay: 0.46,

    // SCROLL KAYBOLMA
    // fadeOutEnd: 0.3 = çabuk kaybolur | 0.7 = uzun süre görünür kalır
    //   (0-1 arası, sayfanın kaçıncı kesrinde tamamen kaybolur)
    // scrollYEnd: scroll'da yukarı kayma — negatif = yukarı (piksel)
    // scrollScaleEnd: scroll'da küçülme — 1.0 (değişmez) → 0.7 (küçük)
    fadeOutEnd:     0.5,
    scrollYEnd:    -200,
    scrollScaleEnd: 0.9,
  },

};

// ====================================================================
//   BURADAN AŞAĞI KOD — DEĞİŞTİRMEN GEREKMİYOR
// ====================================================================

const rand = (min, max) => Math.random() * (max - min) + min;

const useNeonSegments = (overrideColors = null) => {
  const colorsKey = overrideColors ? overrideColors.join(',') : 'default';
  return useMemo(() => {
    const {
      segmentCount, speedMin, speedMax,
      dashArrayMin, dashArrayMax,
      phaseMin, phaseMax,
      colorJitter,
    } = HERO.neon;
    const colors = overrideColors || HERO.neon.colors;

    return Array.from({ length: segmentCount }, (_, idx) => {
      const speed   = rand(speedMin, speedMax);
      const dashArr = rand(dashArrayMin, dashArrayMax);
      const phase   = rand(phaseMin, phaseMax);

      // Sabit ID — mount'ta bir kez üretilir, scroll'da değişmez
      const gradId   = `ng_${idx}_${segmentCount}`;
      const animName = `nf_${idx}_${segmentCount}`;

      const shift   = Math.floor(Math.random() * colors.length);
      const shifted = [...colors.slice(shift), ...colors.slice(0, shift)];
      const last    = shifted.length - 1;
      const stops   = shifted.map((color, i) => {
        const base   = i / last;
        const jitter = (i === 0 || i === last) ? 0 : rand(-colorJitter, colorJitter);
        return { color, offset: `${(Math.min(1, Math.max(0, base + jitter)) * 100).toFixed(1)}%` };
      });

      const glowColor  = shifted[0];
      const period     = dashArr * Math.ceil((phaseMax * 3) / dashArr);
      const startOff   = phase + period;
      const endOff     = phase;
      const segOpacity = rand(0.55, 1.0);

      // Gradient CSS string (sol → sağ)
      const gradientCSS = `linear-gradient(90deg, ${stops.map(s => `${s.color} ${s.offset}`).join(', ')})`;

      return { speed, dashArr, startOff, endOff, gradId, animName, stops, glowColor, segOpacity, gradientCSS };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorsKey]);
};


// ─── SVG hollow neon — fill="none" stroke → gerçekten içi boş ───────
// padding-bottom trick: SVG aspect-ratio CSS'te korunur, JS/ResizeObserver yok.
// textLength YOK → harfler sıkışmaz, font doğal oranında render edilir.
// SVG font-size büyük (200), viewBox buna göre ayarlı → scale edilince vw gibi davranır.
const FluidHollowText = ({ text, width, apiNeon = null, fontSize = null }) => {
  const strokeWidth = apiNeon?.neon_stroke_width ?? HERO.neon.strokeWidth;
  const glowSize    = apiNeon?.neon_glow_size    ?? HERO.neon.glowSize;
  const { showGhostFill, ghostFillOpacity } = HERO.neon;
  const apiColors   = apiNeon?.neon_colors || null;
  const segments    = useNeonSegments(apiColors);
  // uid mount'ta sabit — her render'da yeni string üretilmez
  const uid         = useRef('fh_hero').current;
  const styleRef    = useRef(null);

  const VB_W  = 750;
  const VB_H  = 185;
  const FONT  = 175;
  const BASE  = 172;
  const SW    = strokeWidth;
  const glowPx = parseFloat(glowSize);

  // CSS sadece mount'ta ve segments/glow değişince inject edilir
  useEffect(() => {
    const css = segments.map(({ animName, startOff, endOff, speed, dashArr, gradId, glowColor, segOpacity }) => `
      @keyframes ${animName} {
        0%   { stroke-dashoffset: ${startOff.toFixed(0)}; }
        100% { stroke-dashoffset: ${endOff.toFixed(0)}; }
      }
      .nseg-${uid}-${gradId} {
        stroke-dasharray: ${dashArr.toFixed(0)};
        animation: ${animName} ${speed.toFixed(2)}s linear infinite;
        filter: drop-shadow(0 0 ${glowPx}px ${glowColor});
        opacity: ${segOpacity.toFixed(2)};
      }
    `).join('\n');

    if (!styleRef.current) {
      styleRef.current = document.createElement('style');
      styleRef.current.setAttribute('data-neon', uid);
      document.head.appendChild(styleRef.current);
    }
    styleRef.current.textContent = css;

    return () => {
      if (styleRef.current) {
        styleRef.current.remove();
        styleRef.current = null;
      }
    };
  }, [segments, uid, glowPx]);

  return (
    <div className={`relative ${width}`} style={{ lineHeight: 0, fontSize: fontSize || undefined }}>

      {/* padding-bottom = VB_H/VB_W*100% → aspect-ratio korunur */}
      <div style={{ position: 'relative', width: '100%', paddingBottom: `${(VB_H / VB_W * 100).toFixed(2)}%` }}>
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="xMinYMid meet"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}
        >
          <defs>
            {segments.map(({ gradId, stops }) => (
              <linearGradient key={gradId} id={`${uid}_${gradId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                {stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
              </linearGradient>
            ))}
          </defs>

          {showGhostFill && (
            <text x="0" y={BASE} fontSize={FONT} fontWeight="bold"
                  fill="white" fillOpacity={ghostFillOpacity}
                  textAnchor="start" className="uppercase"
                  style={{ fontFamily: 'inherit' }}>
              {text}
            </text>
          )}

          {segments.map(({ gradId }) => (
            <text key={gradId} x="0" y={BASE}
                  fontSize={FONT} fontWeight="bold"
                  fill="none"
                  stroke={`url(#${uid}_${gradId})`}
                  strokeWidth={SW}
                  textAnchor="start"
                  className={`nseg-${uid}-${gradId} uppercase`}
                  style={{ fontFamily: 'inherit' }}>
              {text}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
};


const CinematicWrapper = ({ children, delay, scrollOpacity, scrollY, scrollScale }) => {
  const { entryDuration, initialY, entryBlur } = HERO.animation;
  return (
    <motion.div
      initial={{ opacity: 0, y: initialY, filter: `blur(${entryBlur})` }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: entryDuration, delay, ease: [0.25, 1, 0.35, 1] }}
      style={{ opacity: scrollOpacity, y: scrollY, scale: scrollScale, willChange: 'transform, opacity' }}
      className="w-full overflow-visible"
    >
      {children}
    </motion.div>
  );
};


export const HeroSection = () => {
  const apiSettings = useSettings();
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end start'] });

  const { fadeOutEnd, scrollYEnd, scrollScaleEnd, line1Delay, line2Delay, line3Delay } = HERO.animation;

  const textOpacity = useTransform(scrollYProgress, [0, fadeOutEnd], [1, 0]);
  const textY       = useTransform(scrollYProgress, [0, 1], [0, scrollYEnd]);
  const textScale   = useTransform(scrollYProgress, [0, 0.5], [1, scrollScaleEnd]);
  const bgScale     = useTransform(scrollYProgress, [0, 1], [1, HERO.media.zoomOnScroll]);

  // Overlay değerleri — apiSettings değişince yeniden hesaplanır, scroll'da değil
  const solidOpacity = useMemo(() => (apiSettings?.hero_overlay_opacity ?? 70) / 100, [apiSettings]);
  const gradientBg   = useMemo(() => {
    const dir  = apiSettings?.hero_gradient_dir  ?? 'to-top';
    const from = (apiSettings?.hero_gradient_from ?? 80) / 100;
    const to   = (apiSettings?.hero_gradient_to   ?? 0)  / 100;
    const gradMap = { 'to-top': 'to top', 'to-bottom': 'to bottom', 'to-right': 'to right' };
    return dir === 'radial'
      ? `radial-gradient(ellipse at center, rgba(0,0,0,${to}) 0%, rgba(0,0,0,${from}) 100%)`
      : `linear-gradient(${gradMap[dir] || 'to top'}, rgba(0,0,0,${from}) 0%, rgba(0,0,0,${to}) 100%)`;
  }, [apiSettings]);

  // Yazılar & görünürlük
  const line1Text    = t('hero', 'line1', apiSettings?.hero_line1 || 'CREATE');
  const line2Text    = t('hero', 'line2', apiSettings?.hero_line2 || 'DIGITAL');
  const line3Text    = t('hero', 'line3', apiSettings?.hero_line3 || 'EXPERIENCES');
  const line1Visible = apiSettings ? apiSettings.hero_line1_visible !== false : true;
  const line2Visible = apiSettings ? apiSettings.hero_line2_visible !== false : true;
  const line3Visible = apiSettings ? apiSettings.hero_line3_visible !== false : true;

  // Font boyutları — sayısal vw/rem değerlerinden CSS clamp() üretilir
  const line1FontSize = useMemo(() => {
    const mob  = apiSettings?.hero_line1_size_mobile  ?? 14;
    const desk = apiSettings?.hero_line1_size_desktop ?? 5;
    return `clamp(${desk * 0.6}rem, ${mob}vw, ${desk}rem)`;
  }, [apiSettings]);
  const line2FontSize = useMemo(() => {
    const mob  = apiSettings?.hero_line2_size_mobile  ?? 11;
    const desk = apiSettings?.hero_line2_size_desktop ?? 8;
    return `clamp(${desk * 0.6}rem, ${mob}vw, ${desk}rem)`;
  }, [apiSettings]);
  const line3FontSize = useMemo(() => {
    const mob  = apiSettings?.hero_line3_size_mobile  ?? 9;
    const desk = apiSettings?.hero_line3_size_desktop ?? 6.5;
    return `clamp(${desk * 0.6}rem, ${mob}vw, ${desk}rem)`;
  }, [apiSettings]);
  // Satır 2 (neon SVG) genişliği font boyutuna orantılı
  const line2Width = useMemo(() => {
    const mob  = apiSettings?.hero_line2_size_mobile  ?? 11;
    const desk = apiSettings?.hero_line2_size_desktop ?? 8;
    return `clamp(${desk * 4}rem, ${mob * 7}vw, ${desk * 7}rem)`;
  }, [apiSettings]);

  const { color: line1Color, weight: line1Weight } = HERO.typography.line1;
  const { color: line3Color, weight: line3Weight } = HERO.typography.line3;
  const { mobileHeight, desktopHeight, containerPaddingTop, lineGap, horizontalPadding, contentAlign } = HERO.layout;

  // iOS Safari: autoPlay React prop'u her zaman çalışmaz.
  // Navigasyon sonrası geri dönüldüğünde video durabilir.
  // Programatik .play() + visibility change listener ile zorla.
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;

    const tryPlay = () => {
      if (vid.paused) {
        vid.play().catch(() => {}); // izin reddedilirse sessizce geç
      }
    };

    tryPlay();

    // Kullanıcı tab'a geri döndüğünde veya sayfa görünür olduğunda tekrar çalıştır
    const handleVisibility = () => {
      if (!document.hidden) tryPlay();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Sayfa gezmesi sonrası geri dönüşlerde (pageshow / bfcache)
    const handlePageShow = (e) => {
      if (e.persisted) tryPlay();
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [apiSettings]);

  return (
    <section
      id="hero"
      ref={containerRef}
      className={`relative ${mobileHeight} ${desktopHeight} overflow-hidden bg-white dark:bg-black flex flex-col transition-colors duration-500`}
    >
      <motion.div className="absolute inset-0 z-0" style={{ scale: bgScale, willChange: "transform" }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          src={apiSettings?.hero_video_src || HERO.media.videoSrc}
          style={{ willChange: 'transform' }}
          onCanPlay={() => { videoRef.current?.play().catch(() => {}); }}
        />
        {/* Solid overlay */}
        <div className="absolute inset-0 dark:block hidden" style={{ backgroundColor: `rgba(0,0,0,${solidOpacity})` }} />
        <div className="absolute inset-0 dark:hidden block" style={{ backgroundColor: `rgba(255,255,255,${solidOpacity})` }} />
        {/* Gradient overlay */}
        <div className="absolute inset-0 dark:block hidden" style={{ background: gradientBg }} />
        <div className="absolute inset-0 dark:hidden block" style={{ background: gradientBg.replace(/0,0,0,/g, '255,255,255,') }} />
      </motion.div>

      <div className={`relative z-10 h-full flex flex-col justify-start ${horizontalPadding} ${containerPaddingTop}`}>
        <div className={`w-full max-w-7xl flex flex-col ${contentAlign}`}>

          {line1Visible && (
          <div className={lineGap}>
            <CinematicWrapper delay={line1Delay} scrollOpacity={textOpacity} scrollY={textY} scrollScale={textScale}>
              <h1 className={`font-display ${line1Weight} text-black dark:${line1Color} tracking-tighter leading-none uppercase whitespace-nowrap transition-colors duration-500`}
                  style={{ fontSize: line1FontSize }}>
                {line1Text}
              </h1>
            </CinematicWrapper>
          </div>
          )}

          {line2Visible && (
          <div className={`${lineGap} w-full`}>
            <CinematicWrapper delay={line2Delay} scrollOpacity={textOpacity} scrollY={textY} scrollScale={textScale}>
              <FluidHollowText text={line2Text} width={line2Width} apiNeon={apiSettings} fontSize={line2FontSize} />
            </CinematicWrapper>
          </div>
          )}

          {line3Visible && (
          <div className={lineGap}>
            <CinematicWrapper delay={line3Delay} scrollOpacity={textOpacity} scrollY={textY} scrollScale={textScale}>
              <h2 className={`font-display ${line3Weight} text-black/50 dark:${line3Color} tracking-tighter leading-none uppercase whitespace-nowrap transition-colors duration-500`}
                  style={{ fontSize: line3FontSize }}>
                {line3Text}
              </h2>
            </CinematicWrapper>
          </div>
          )}

        </div>
      </div>
    </section>
  );
};